import { memo, useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { RoutePath } from 'consts/RoutePath';
import { Hr } from 'components/atoms/Hr';
import classes from './override.module.scss';
import { UserWithGroupAuth, GroupWithLoginUser, callChangeGroupAdminApi } from 'requests/RequestGroup';
import { CircularProgress } from '@material-ui/core';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { ApiError } from 'requests/RequestBase';
import {
	callCheckOnetimePasswordApi,
	callSendOnetimePasswordMailApi,
	CheckOnetimePasswordResData,
} from 'requests/RequestUser';
import { findGroupAuthByKey, isMember, isAdmin, GroupAuth } from 'consts/GroupAuthConst';
import { BirthDay } from 'components/molecules/BirthDay';
import { ApiErrorCode, findErrorMessageByApiError } from 'consts/ApiErrorCode';

type Props = {};
type ChangeRepresentativeDto = {
	group: GroupWithLoginUser;
};

export const ChangeRepresentativeTemplate = memo((_props: Props) => {
	const history = useHistory<ChangeRepresentativeDto>();
	const dto = history.location.state;
	const [page, setPage] = useState(0);
	const [processLoading, setProcessLoading] = useState(false);
	const [formData, setFormData] = useState({ onetime_password: '' });
	const [formErrors, setFormErrors] = useState({ onetime_password: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, onetime_password: event.target.value }));
	};

	const sendMail = async () => {
		setProcessLoading(true);
		// メール送信API
		const sendMailResult = await callSendOnetimePasswordMailApi({
			mailaddress: dto.group.loginUser.mailaddress_1,
			send_type: 4,
		});
		if (sendMailResult.status !== HttpStatus.OK) {
			history.push(RoutePath.ERROR);
			setProcessLoading(false);
			return;
		}
		setPage(page + 1);
		setProcessLoading(false);
	};

	const checkOnetimePassword = async () => {
		setProcessLoading(true);
		// ワンタイムパスワード検証
		if (!formData.onetime_password.trim()) {
			setFormErrors((prev) => ({ ...prev, onetime_password: ReactError.E0001 }));
			setProcessLoading(false);
			return;
		}
		const checkOnetimePasswordResult = await callCheckOnetimePasswordApi({
			mailaddress: dto.group.loginUser.mailaddress_1,
			onetimepass: formData.onetime_password,
		});

		if (checkOnetimePasswordResult.status !== HttpStatus.OK) {
			const error = checkOnetimePasswordResult.data as ApiError;
			if (error.error_code === ApiErrorCode.E1157.code) {
				setFormErrors((prev) => ({ ...prev, onetime_password: findErrorMessageByApiError(error) }));
				setProcessLoading(false);
				return;
			}
			history.push(RoutePath.ERROR);
			return;
		}
		const data = checkOnetimePasswordResult.data as CheckOnetimePasswordResData;
		if (!data.exist) {
			setFormErrors((prev) => ({ ...prev, onetime_password: 'ワンタイムパスワードが正しくありません。' }));
			setProcessLoading(false);
			return;
		}
		setPage(page + 1);
		setProcessLoading(false);
	};

	const [nextAdmin, setNextAdmin] = useState<UserWithGroupAuth>(undefined);
	const selectNextAdmin = (user: UserWithGroupAuth) => {
		setNextAdmin(user);
		setPage(page + 1);
	};

	const [timer, setTimer] = useState(undefined);
	const backToMyPage = () => {
		clearTimeout(timer);
		history.push(RoutePath.MY_PAGE);
	};
	const changeAdmin = async () => {
		setProcessLoading(true);

		// 代表者変更API
		const result = await callChangeGroupAdminApi(dto.group.group_id, { gidpk: nextAdmin.gidpk });
		if (result.status !== HttpStatus.OK) {
			const errorData = result.data as ApiError;
			setActionError(errorData.message);
			setProcessLoading(false);
			return;
		}

		const timer = setTimeout(function () {
			history.push(RoutePath.MY_PAGE);
		}, 5000);
		setTimer(timer);

		setPage((prev) => prev + 1);
		setProcessLoading(false);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = '代表者を変更する';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<main>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>
							<Link to={RoutePath.FAMILY_INFO}>家族情報</Link>
						</li>
						<li>代表者を変更する</li>
					</ul>
				</div>
				<h1 className="page-head">代表者を変更する</h1>
				{page === 0 && (
					<>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>メールアドレスによる本⼈確認</span>
							</div>
							<div className="section-txt-box">
								<div className="txt">
									<div>ここから先へ進むには、メールアドレスによる本⼈確認が必要となります。</div>
									<div>登録されているメールアドレスをご確認の上「メールを送信する」ボタンを押してください。</div>
									<div>※送付先が違う場合は、マイページから変更してください</div>
								</div>
							</div>
							<div className="info-box">
								<div className="info-head">登録されているメールアドレス</div>
								<div className="info-head">{dto.group.loginUser.mailaddress_1}</div>
							</div>
							<div className="btn-box">
								<div className="btn gr">
									{processLoading ? <CircularProgress /> : <button onClick={sendMail}>メールを送信する</button>}
								</div>
							</div>
						</div>
					</>
				)}
				{page === 1 && (
					<div className="section-in" ref={scrollRef}>
						<div className="section-head">ワンタイムパスワードの入力</div>
						<div className="section-txt-box">
							<div className="txt">
								<div>ご登録いただいたメールアドレスへメールをお送りしました。</div>
								<div className={classes.onetimePassSubTitle}>件名：【GakkenID】ご本人様確認のご案内</div>
								<div>
									2時間以内にメールに記載されたワンタイムパスワードを入力し、「確認」ボタンから代表者変更をお願いいたします。
								</div>
							</div>
						</div>

						{isFormError ? (
							<div className="error-box" ref={errorRef}>
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.onetime_password !== '' ? (
											<p>【ワンタイムパスワード】{formErrors.onetime_password}</p>
										) : null}
									</div>
								</>
							</div>
						) : null}

						<div className="form-box">
							<div className="form-head">
								<label htmlFor="">ワンタイムパスワード</label>
							</div>
							<div className="form-input">
								<input
									type="password"
									name="onetime_password"
									value={formData.onetime_password}
									onChange={handleChangeValue}
								/>
							</div>
							<p className="error">{formErrors.onetime_password}</p>
						</div>
						<div className="btn-box">
							<div className="btn gr">
								{processLoading ? <CircularProgress /> : <button onClick={checkOnetimePassword}>確認</button>}
							</div>
						</div>
					</div>
				)}
				{page === 2 && (
					<div className="section-in" style={{ marginTop: 50 }}>
						<div className="section-head">
							<span>代表者にしたいユーザーを選択</span>
						</div>
						<div className="section-txt-box">
							<div className="txt">
								<p>代表者にしたいユーザーを選択してください。</p>
								<p>{`※${GroupAuth.MEMBER.name}を選択することはできません`}</p>
							</div>
						</div>
						<div className="info-box">
							<Hr />
							{dto.group.gid_list
								.filter((u) => !isAdmin(findGroupAuthByKey(u)))
								.map((u) => {
									const auth = findGroupAuthByKey(u);
									return (
										<>
											<div className={`${classes.memberRow} ${isMember(auth) ? classes.memberInfoDisabled : ''}`}>
												<div
													className={`${classes.memberRowDataColumn} ${classes.iconColumn} ${classes.iconDataColumn}`}
												>
													<img className={classes.memberIcon} src={`images/${auth.image}`} alt="" />
													<div className={classes.memberLabel}>{auth.name}</div>
												</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.nameColumn} ${classes.nameDataColumn}`}
												>{`${u.lastname}　${u.firstname}`}</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.birthColumn} ${classes.birthDataColumn}`}
												>
													<BirthDay birth={u.birth} />
												</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.linkColumn} ${classes.iconDataColumn}`}
												>
													{!isMember(auth) && (
														<button
															className={classes.selectRepresentative}
															onClick={() => {
																selectNextAdmin(u);
															}}
														>
															&gt;
														</button>
													)}
												</div>
											</div>
											<Hr />
										</>
									);
								})}
						</div>
						<div className="btn-box">
							<div className="btn wh">
								<button
									onClick={() => {
										history.push(RoutePath.MY_PAGE);
									}}
								>
									キャンセル
								</button>
							</div>
						</div>
					</div>
				)}
				{page === 3 && (
					<div className="section-in" style={{ marginTop: 50 }}>
						<div className="section-head error-box">
							<p>注意事項をご確認ください</p>
						</div>
						<div className="section-txt-box">
							<div className="txt">
								<div>代表者を変更すると以下のことができなくなります。</div>
								<ul>
									<li>すでにGakkenIDを保有している「ユーザーの招待」</li>
									<li>所属している⼀般ユーザーの「決済権のコントロール」</li>
									<li>{`${GroupAuth.MEMBER.name}以外の「ユーザーの管理」`}</li>
								</ul>
							</div>
						</div>

						{isActionError ? (
							<div className="error-box" ref={errorRef}>
								<p>{actionError}</p>
							</div>
						) : null}

						<div className="info-box">
							<Hr />
							<div className={classes.memberRow}>
								<div className={`${classes.memberRowDataColumn} ${classes.iconColumn} ${classes.iconDataColumn}`}>
									<span className={classes.selectRepresentativeIcon}>変更前</span>
								</div>
								<div className={`${classes.memberRowDataColumn} ${classes.iconColumn} ${classes.iconDataColumn}`}>
									<img className={classes.memberIcon} src={`images/${GroupAuth.ADMIN.image}`} alt="" />
									<div className={classes.memberLabel}>代表者</div>
								</div>
								<div
									className={`${classes.memberRowDataColumn} ${classes.nameColumn} ${classes.nameDataColumn}`}
								>{`${dto.group.loginUser.lastname}　${dto.group.loginUser.firstname}`}</div>
								<div className={`${classes.memberRowDataColumn} ${classes.birthColumn} ${classes.birthDataColumn}`}>
									<BirthDay birth={dto.group.loginUser.birth} />
								</div>
							</div>
							<Hr />
							<div className={classes.memberRow}>
								<div className={'info-txt ' + classes.selectRepresentativeColIcon}>↓</div>
							</div>
							<Hr />
							<div className={classes.memberRow}>
								<div className={`${classes.memberRowDataColumn} ${classes.iconColumn} ${classes.iconDataColumn}`}>
									<span className={classes.selectRepresentativeIcon}>変更後</span>
								</div>
								<div className={`${classes.memberRowDataColumn} ${classes.iconColumn} ${classes.iconDataColumn}`}>
									<img className={classes.memberIcon} src={`images/${findGroupAuthByKey(nextAdmin).image}`} alt="" />
									<div className={classes.memberLabel}>{findGroupAuthByKey(nextAdmin).name}</div>
								</div>
								<div
									className={`${classes.memberRowDataColumn} ${classes.nameColumn} ${classes.nameDataColumn}`}
								>{`${nextAdmin.lastname}　${nextAdmin.firstname}`}</div>
								<div className={`${classes.memberRowDataColumn} ${classes.birthColumn} ${classes.birthDataColumn}`}>
									<BirthDay birth={nextAdmin.birth} />
								</div>
							</div>
							<Hr />
						</div>
						<div className="btn-box">
							{processLoading ? (
								<CircularProgress />
							) : (
								<>
									<div className="btn wh">
										<button
											onClick={() => {
												setPage(page - 1);
											}}
										>
											戻る
										</button>
									</div>
									<div className="btn gr">
										<button onClick={changeAdmin}>変更</button>
									</div>
								</>
							)}
						</div>
					</div>
				)}
				{page === 4 && (
					<div className="section-in" style={{ marginTop: 50 }}>
						<div className="thankyou-box">
							<img src="images/img-complete.svg" alt="完了しました" />
						</div>
						<p>
							<div>5秒後に自動的にマイページTOPへ戻ります</div>
						</p>
						<div className="btn-box">
							<div className="btn gr">
								<button onClick={backToMyPage}>マイページTOPへ戻る</button>
							</div>
						</div>
					</div>
				)}
			</section>
		</main>
	);
});
